import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-89f5a5d8"),n=n(),_popScopeId(),n)
const _hoisted_1 = { id: "preview-only-page" }
const _hoisted_2 = { class: "controller-wrapper" }

import { onMounted, ref, computed } from "vue";

/**
 * 予約プレビューコンポーネント
 * 選択内容のプレビュー表示に使用
 */
import BookingPreview from "./BookingPreview.vue";

/**
 * ルーティングユーティリティ
 * 画面遷移の制御に使用
 */
import RouterUtils from "@/common/utils/router.util";

/**
 * ルーティング定数
 * 画面遷移先の定義に使用
 */
import RouterConstants from "../constants/router.constants";

/**
 * 予約設定サービス
 * 予約設定の取得に使用
 */
import { BookingSettingService } from "@/customer/services";

/**
 * Vuexストア
 * アプリケーション全体の状態管理に使用
 */
import { useStore } from "vuex";

/**
 * HTTPクライアント
 * APIリクエストの送信に使用
 */
import axios from "axios";

/**
 * APIルーティング定数
 * APIエンドポイントの定義に使用
 */
import ApiRouterConstants from "@/common/constants/api.router.constants";

/**
 * スタッフの型定義
 */
import { IStaff } from "@/clinic/typings/entities/staff";

/**
 * スタッフ配分サービス
 * スタッフの自動配分処理に使用
 */
import StaffDistributionService from "@/clinic/services/customer/staff-distribution.services";

/**
 * クリニックパートナー
 * パートナー種別の判定に使用
 */
import { ClinicPartner } from "@/clinic/typings/enums/clinic";

/**
 * Vuexストアインスタンス
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'BookingPreviewOnly',
  setup(__props) {

/**
 * 予約プレビューのみを表示する画面コンポーネント
 * 予約内容の確認と、カレンダー画面への遷移を行う画面
 */

/**
 * Vueの基本機能
 * コンポーネントのライフサイクルとリアクティブな状態管理に使用
 */
const store = useStore();

/**
 * 予約設定
 * @type {Ref<Record<string, any>>}
 */
const bookingSetting = ref<Record<string, any>>({});

/**
 * メニューオプション有無
 * @type {Ref<boolean>}
 */
const hasMenuOption = ref(false);

/**
 * スタッフ有無
 * @type {Ref<boolean>}
 */
const hasStaff = ref(false);

/**
 * コンポーネントマウント時の処理
 * 予約設定の初期化を行う
 */
onMounted(async () => {
  // 予約設定を取得して初期化
  const { data: settings } = await BookingSettingService.loadSetting(store.getters["line_account/clinicId"]);
  if (settings?.value) {
    store.commit("bookingSetting/setBookingSetting", settings.value);
    bookingSetting.value = settings.value;
  }

  // メニューオプション情報を取得し、オプションが存在するか判定
  const resOption = await axios.get(ApiRouterConstants.API_MENU_OPTION, {
    params: {
      clinic_id: store.getters["line_account/clinicId"],
      menu_id: store.getters["booking/selectedMenu"].id,
    },
    headers: { Accept: "application/json" },
  });
  // オプションが1件以上あれば true
  hasMenuOption.value = Array.isArray(resOption.data) ? resOption.data.length > 0 : !!resOption.data;

  // スタッフ一覧を取得し、選択中メニューに紐づくスタッフがいるか判定
  const resStaff = await axios.get(ApiRouterConstants.API_STAFF, {
    params: { clinic_id: store.getters["line_account/clinicId"] },
    headers: { Accept: "application/json" },
  });
  // 選択中メニューIDに紐づくスタッフのみを抽出
  const selectedMenu = store.getters["booking/selectedMenu"];
  const staffList = resStaff.data.filter((staff: any) => staff.medical_menu_ids.includes(selectedMenu.id));
  // スタッフが1人以上いれば true
  hasStaff.value = staffList.length > 0;
});

/**
 * 指名予約モードかどうか
 * @type {ComputedRef<boolean>}
 */
const isNominate = computed(() => bookingSetting.value.option === "nominate");

/**
 * 予約プレビューに表示する属性一覧
 * @type {string[]}
 */
const bookingPreviewAttributes = ["staffName", "orderPrice", "menuName"];

/**
 * カレンダー画面への遷移処理
 * スタッフ情報の設定と自動配分を行い、カレンダー画面に遷移する
 */
const goToCalendar = async () => {
  // スタッフ情報の設定を確実に行う
  const selectedStaffId = store.getters["booking/selectedStaffId"];

  // 指名予約の場合、スタッフ情報を先に取得
  if (isNominate.value && selectedStaffId) {
    // スタッフ一覧を取得
    const res = await axios.get(ApiRouterConstants.API_STAFF, {
      params: { clinic_id: store.getters["line_account/clinicId"] },
      headers: { Accept: "application/json" },
    });
    // 選択されたスタッフを探す
    const staff = res.data.find((s: IStaff) => s.id === selectedStaffId);
    if (staff) {
      // スタッフ情報を設定
      store.commit("booking/selectStaff", staff);
      store.commit("booking/setStaffOrderPrice", staff.order_price);
      store.commit("booking/setUseDefaultStaff", false);
      store.commit("booking/setSelectedStaffId", staff.id);
      store.commit("booking/setStaffId", staff.id);
    }
  } else {
    // 自動配分の場合、デフォルトスタッフを設定
    const res = await axios.get(ApiRouterConstants.API_STAFF, {
      params: { clinic_id: store.getters["line_account/clinicId"] },
      headers: { Accept: "application/json" },
    });
    // デフォルトスタッフを探す
    const defaultStaff = res.data.find((s: IStaff) => s.default_staff);
    if (defaultStaff) {
      store.commit("booking/setStaffId", defaultStaff.id);
    }
  }

  // 自動スタッフ配分処理を実行
  const { data } = await StaffDistributionService.getStaff({
    clinic_id: store.getters["line_account/clinicId"],
    medical_menu_id: store.getters["booking/selectedMenu"].id,
  });
  const distributedStaffIds = data.value as unknown as number[];

  // 自動スタッフ配分データを設定
  if (distributedStaffIds && distributedStaffIds.length > 0) {
    // 配分リストが存在する場合
    store.commit("booking/setAutoStaffDistributionData", distributedStaffIds);
    if (!selectedStaffId) {
      // 指名がない場合は自動配分を有効化
      store.commit("bookingSetting/setAutoStaffDistribution", true);
    }
    if (selectedStaffId && !distributedStaffIds.includes(selectedStaffId)) {
      // 指名スタッフが配分リストに含まれていない場合は追加
      store.commit("booking/setAutoStaffDistributionData", [...distributedStaffIds, selectedStaffId]);
    }
  } else if (selectedStaffId) {
    // 配分リストが空で指名がある場合は指名スタッフのみを設定
    store.commit("booking/setAutoStaffDistributionData", [selectedStaffId]);
  }

  // カレンダー画面に遷移
  RouterUtils.goTo(RouterConstants.BOOKING_CALENDAR_ROUTER_NAME);
};

/**
 * 戻るボタンクリック時の処理
 * Ripicleパートナーで指名予約の場合、紐付きスタッフとオプションの有無で判断できない場合はメニュー選択画面に戻る
 * それ以外の場合は指名予約かどうかとオプションの有無に応じて戻り先を決定する
 */
const back = () => {
  const selectedMenu = store.getters["booking/selectedMenu"];
  const isRipicle = bookingSetting.value?.partner === ClinicPartner.Ripicle;

  if (isRipicle && isNominate.value) {
    if (hasStaff.value) {
      // スタッフが1人以上いる → スタッフ選択画面に戻る
      RouterUtils.goTo(RouterConstants.BOOKING_ROUTER_NAME);
    } else if (hasMenuOption.value) {
      // スタッフがいない & オプションあり → オプション選択画面に戻る
      RouterUtils.goTo(RouterConstants.BOOKING_OPTION_ROUTER_NAME);
    } else {
      // スタッフがいない & オプションなし → メニュー選択画面に戻る
      RouterUtils.goTo(RouterConstants.BOOKING_MENU_ROUTER_NAME);
    }
  } else if (isNominate.value) {
    // Ripicle以外で指名予約の場合はスタッフ選択画面に戻る
    RouterUtils.goTo(RouterConstants.BOOKING_ROUTER_NAME);
  } else if (selectedMenu?.has_options) {
    // オプションありの場合はオプション選択画面に戻る
    RouterUtils.goTo(RouterConstants.BOOKING_OPTION_ROUTER_NAME);
  } else {
    // オプションなしの場合はメニュー選択画面に戻る
    RouterUtils.goTo(RouterConstants.BOOKING_MENU_ROUTER_NAME);
  }
};

return (_ctx: any,_cache: any) => {
  const _component_Button = _resolveComponent("Button")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(BookingPreview, { previewAttributes: bookingPreviewAttributes }),
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_component_Button, {
        class: "btn cancel-btn w-full",
        onClick: back
      }, {
        default: _withCtx(() => [
          _createTextVNode("戻る")
        ]),
        _: 1
      }),
      _createVNode(_component_Button, {
        class: "btn accept-btn w-full",
        onClick: goToCalendar
      }, {
        default: _withCtx(() => [
          _createTextVNode("日時選択へ")
        ]),
        _: 1
      })
    ])
  ]))
}
}

})